import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { Toaster } from "sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppFAB from "./components/WhatsAppFAB";
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminStores from "./pages/admin/AdminStores";
import AdminClients from "./pages/admin/AdminClients";
import AdminPromotions from "./pages/admin/AdminPromotions";
import AdminFinances from "./pages/admin/AdminFinances";
import AdminReports from "./pages/admin/AdminReports";
import AdminProduction from "./pages/admin/AdminProduction";
import AdminDeliveries from "./pages/admin/AdminDeliveries";
import AdminProviders from "./pages/admin/AdminProviders";
import AdminSurveys from "./pages/admin/AdminSurveys";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminUsersAndLogs from "./pages/admin/AdminUsersAndLogs";
import AdminCashRegister from "./pages/admin/AdminCashRegister";
import LegalInfoPage from "./pages/LegalInfoPage";

import { Menu, Home, Clock, Sparkles, Snowflake, Heart, Leaf, HelpCircle, MapPin, Calendar, ShoppingBag } from 'lucide-react';
import { useState, useEffect } from 'react';

function PublicLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    // Initial state based on window width (768px matches Tailwind 'md')
    setSidebarOpen(window.innerWidth >= 768);

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const primaryLinks = [
    { label: 'Inicio', to: '/', icon: Home },
    { label: 'Todos los Productos', to: '/productos', icon: ShoppingBag },
    { label: 'Mi Pedido', to: '/tracking', icon: Clock },
  ];

  const categoryLinks = [
    { label: 'Pide Para Hoy', to: '/productos?category=pide-hoy', icon: Clock },
    { label: 'Más Populares', to: '/productos?filter=popular', icon: Heart },
    { label: 'Primera Comunión', to: '/productos?category=comunion', icon: Snowflake },
    { label: 'Grados', to: '/productos?category=grados', icon: Leaf },
    { label: 'Refrigeradas', to: '/productos?category=refrigeradas', icon: Snowflake },
    { label: 'Refrigeradas Premium', to: '/productos?category=refrigeradas-premium', icon: Snowflake },
    { label: 'Especiales', to: '/productos?category=tortas-especiales', icon: Sparkles },
    { label: 'Clasicas', to: '/productos?category=clasicas', icon: Heart },
    { label: 'Saludables', to: '/productos?category=saludables', icon: Leaf },
    { label: 'En 45 min x Rappi', to: 'https://www.rappi.com.co/restaurantes/900163992-keiks-reposteria', icon: Clock, external: true },
  ];

  const infoLinks = [
    { label: 'Eventos', to: '/#eventos', icon: Calendar },
    { label: 'Nuestras Tiendas', to: '/#tiendas', icon: MapPin },
    { label: 'Preguntas Frecuentes', to: '/#faq', icon: HelpCircle },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#fcfbf9] font-body">
      {/* Top Banner with direct Rappi Link */}
      <a
        href="https://www.rappi.com.co/restaurantes/900163992-keiks-reposteria"
        target="_blank"
        rel="noopener noreferrer"
        className="bg-[#f0c294]/20 border-b border-[#f0c294]/30 text-[#8a5d2e] py-1.5 text-center text-xs font-bold font-body z-40 block hover:bg-[#f0c294]/30 transition-colors"
      >
        Envio express en 45 minutos por Rappi aquí
      </a>

      {/* Top Header/Navbar with sidebar toggler */}
      <Navbar onToggleSidebar={() => setSidebarOpen(prev => !prev)} sidebarOpen={sidebarOpen} />

      <div className="flex-1 flex flex-row relative">
        {/* Backdrop for mobile when sidebar is open */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-30 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Left Sidebar - Controlled dynamically by sidebarOpen */}
        {sidebarOpen && (
          <aside className="fixed top-14 left-0 z-40 w-64 h-[calc(100vh-3.5rem)] bg-[#fcfbf9] border-r border-[#a0d5ba]/40 shadow-xl md:shadow-none p-4 space-y-4 overflow-y-auto md:relative md:top-0 md:h-auto md:z-auto md:bg-[#a0d5ba]/30 md:flex md:flex-col shrink-0 transition-all duration-300">
            {/* Sección: Navegación Principal */}
            <div>
              <h3 className="px-3 mb-2 text-xs font-bold uppercase tracking-wider text-[#46371c]/50">Menú</h3>
              <div className="space-y-1">
                {primaryLinks.map((link, idx) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={idx}
                      href={link.to}
                      onClick={() => {
                        if (window.innerWidth < 768) setSidebarOpen(false);
                      }}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl font-body text-sm font-medium text-[#46371c] hover:bg-[#a0d5ba]/50 hover:text-[#2d2312] transition-all"
                    >
                      <Icon className="w-4 h-4 shrink-0 text-[#46371c]/70" />
                      <span>{link.label}</span>
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Separador */}
            <hr className="border-[#a0d5ba]/20" />

            {/* Sección: Categorías */}
            <div>
              <h3 className="px-3 mb-2 text-xs font-bold uppercase tracking-wider text-[#46371c]/50">Categorías</h3>
              <div className="space-y-1">
                {categoryLinks.map((link, idx) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={idx}
                      href={link.to}
                      onClick={() => {
                        if (window.innerWidth < 768) setSidebarOpen(false);
                      }}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl font-body text-sm font-medium text-[#46371c] hover:bg-[#a0d5ba]/50 hover:text-[#2d2312] transition-all"
                    >
                      <Icon className="w-4 h-4 shrink-0 text-[#46371c]/70" />
                      <span>{link.label}</span>
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Separador */}
            <hr className="border-[#a0d5ba]/20" />

            {/* Sección: Información */}
            <div>
              <h3 className="px-3 mb-2 text-xs font-bold uppercase tracking-wider text-[#46371c]/50">Información</h3>
              <div className="space-y-1">
                {infoLinks.map((link, idx) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={idx}
                      href={link.to}
                      target={link.external ? "_blank" : undefined}
                      rel={link.external ? "noopener noreferrer" : undefined}
                      onClick={() => {
                        if (window.innerWidth < 768) setSidebarOpen(false);
                      }}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl font-body text-sm font-medium text-[#46371c] hover:bg-[#a0d5ba]/50 hover:text-[#2d2312] transition-all"
                    >
                      <Icon className="w-4 h-4 shrink-0 text-[#46371c]/70" />
                      <span>{link.label}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          </aside>
        )}

        {/* Main Content Area */}
        <main className="flex-1 min-w-0 bg-[#fcfbf9]">
          {children}
        </main>
      </div>

      <Footer />
      <WhatsAppFAB />
    </div>
  );
}

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
            <Route path="/productos" element={<PublicLayout><ProductsPage /></PublicLayout>} />
            <Route path="/producto/:id" element={<PublicLayout><ProductDetailPage /></PublicLayout>} />
            <Route path="/carrito" element={<PublicLayout><CartPage /></PublicLayout>} />
            <Route path="/checkout" element={<PublicLayout><CheckoutPage /></PublicLayout>} />
            <Route path="/tracking" element={<PublicLayout><OrderTrackingPage /></PublicLayout>} />
            <Route path="/legal" element={<PublicLayout><LegalInfoPage /></PublicLayout>} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="pedidos" element={<AdminOrders />} />
              <Route path="productos" element={<AdminProducts />} />
              <Route path="tiendas" element={<AdminStores />} />
              <Route path="produccion" element={<AdminProduction />} />
              <Route path="entregas" element={<AdminDeliveries />} />
              <Route path="clientes" element={<AdminClients />} />
              <Route path="usuarios" element={<AdminUsersAndLogs />} />
              <Route path="promociones" element={<AdminPromotions />} />
              <Route path="finanzas" element={<AdminFinances />} />
              <Route path="caja" element={<AdminCashRegister />} />
              <Route path="reportes" element={<AdminReports />} />
              <Route path="proveedores" element={<AdminProviders />} />
              <Route path="encuestas" element={<AdminSurveys />} />
              <Route path="configuracion" element={<AdminSettings />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  </SettingsProvider>
  );
}

export default App;
